import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Badge,
  CircularProgress,
  Typography as MuiTypography,
  Chip,
  Paper,
  InputBase,
  Popper,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
  Menu as MenuIcon,
  Logout,
  ShoppingCart as CartIcon,
  Chat as ChatIcon,
  Notifications as NotificationIcon,
  CalendarToday as CalendarIcon,
  Tune as FilterIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Restaurant as RestaurantIcon,
  LocalDining as LocalDiningIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { UserType } from '../types';
import { useCart } from '../contexts/CartContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useVendorNotifications } from '../contexts/VendorNotificationContext';
import { useSearch } from '../contexts/SearchContext';
import { Notification } from '../types/notification';
import Cart from './Cart';
import ChaaviNotificationPanel from './ChaaviNotificationPanel';
import ChaaviClientProfileMenu from './ChaaviClientProfileMenu';
import ChaaviEditProfileModal from './ChaaviEditProfileModal';
import ChaaviChangePasswordModal from './ChaaviChangePasswordModal';
import { APP_DISPLAY_NAME } from '../config/brand';
import chatService from '../services/chatService';
import BusinessService from '../services/businessService';
import UserService from '../services/userService';
import VendorService from '../services/vendorService';
import AuthService from '../services/authService';

// Responsive drawer width - scales with viewport
// Note: drawerWidth constant removed as we now use clamp() directly in sx props

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUserProfile } = useAuth();
  const { getCartItemCount, isCartOpen, openCart, closeCart } = useCart();
  const { 
    notifications: clientNotifications, 
    unreadCount: clientNotificationCount, 
    loading: loadingNotifications,
    markAsRead,
    markAllAsRead: markAllClientNotificationsRead,
  } = useNotifications();
  const {
    notifications: vendorNotifications,
    unreadCount: vendorNotificationCount,
    loading: loadingVendorNotifications,
    markAsRead: markVendorNotificationAsRead,
    markAllAsRead: markAllVendorNotificationsRead,
    refreshNotifications: refreshVendorNotifications,
  } = useVendorNotifications();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [vendorNotificationMenuAnchor, setVendorNotificationMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [unreadChatCount, setUnreadChatCount] = React.useState(0);
  const [isCateringBusiness, setIsCateringBusiness] = React.useState(false);
  const [vendorCategories, setVendorCategories] = React.useState<string[]>([]);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = React.useState(false);
  const [deletingAccount, setDeletingAccount] = React.useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = React.useState(false);
  const [changingPassword, setChangingPassword] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [clientEditProfileOpen, setClientEditProfileOpen] = React.useState(false);
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [editEmail, setEditEmail] = React.useState('');
  const [editProfileSaving, setEditProfileSaving] = React.useState(false);
  const [editProfileError, setEditProfileError] = React.useState('');
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error' | 'info' | 'warning'>('info');
  const { 
    searchQuery, 
    setSearchQuery, 
    searchAnchorEl, 
    setSearchAnchorEl, 
    showSuggestions, 
    setShowSuggestions,
    searchSuggestions,
  } = useSearch();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchor(null);
  };

  const handleVendorNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setVendorNotificationMenuAnchor(event.currentTarget);
    // Refresh vendor notifications when menu opens
    if (user && user.userType === 'VENDOR') {
      refreshVendorNotifications();
    }
  };

  const handleVendorNotificationMenuClose = () => {
    setVendorNotificationMenuAnchor(null);
  };

  const handleVendorNotificationClick = async (notification: Notification) => {
    try {
      await markVendorNotificationAsRead(notification.notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    
    // Close the dropdown
    handleVendorNotificationMenuClose();
    
    // Navigate to notifications tab (index 0) with the notification ID to highlight it
    navigate('/vendor-orders-notifications', { 
      state: { 
        activeTab: 0, // Notifications tab
        notificationId: notification.notificationId,
        orderId: notification.orderId 
      } 
    });
  };

  const handleClientNotificationDismiss = async (notification: Notification) => {
    try {
      await markAsRead(notification.notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleVendorNotificationDismiss = async (notification: Notification) => {
    try {
      await markVendorNotificationAsRead(notification.notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Always mark as read when notification is clicked/opened
    // The backend will handle if it's already read
    try {
      await markAsRead(notification.notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Continue navigation even if marking as read fails
    }
    
    // Close the dropdown
    handleNotificationMenuClose();
    
    // If notification has an orderId, navigate to My Orders tab to show all orders
    // Otherwise, navigate to Notifications tab
    if (notification.orderId) {
      navigate('/client-dashboard', { 
        state: { 
          activeTab: 1, // My Orders tab - show all orders
          orderId: notification.orderId, // Keep orderId for reference but don't auto-open dialog
          notificationId: notification.notificationId
        } 
      });
    } else {
      navigate('/client-dashboard', { 
        state: { 
          activeTab: 2, // Notifications tab
          notificationId: notification.notificationId
        } 
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleUserMenuClose();
  };

  React.useEffect(() => {
    if (clientEditProfileOpen && user?.userType === 'CLIENT') {
      setEditFirstName(user.firstName || '');
      setEditLastName(user.lastName || '');
      setEditEmail(user.email || '');
      setEditProfileError('');
    }
  }, [clientEditProfileOpen, user]);

  const handleSaveClientProfile = async () => {
    if (!user || user.userType !== 'CLIENT') return;
    const fn = editFirstName.trim();
    const ln = editLastName.trim();
    const em = editEmail.trim();
    if (!fn || !em) {
      setEditProfileError('First name and email are required.');
      return;
    }
    setEditProfileSaving(true);
    setEditProfileError('');
    try {
      await UserService.updateUser(user.phoneNumber, {
        firstName: fn,
        lastName: ln,
        email: em,
        phoneNumber: user.phoneNumber,
        userType: user.userType as UserType,
      });
      await refreshUserProfile();
      setClientEditProfileOpen(false);
      setSnackbarMessage('Profile updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update profile';
      setEditProfileError(msg);
    } finally {
      setEditProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordError('');
      
      await AuthService.changePassword(currentPassword, newPassword);
      
      // Show success message
      setSnackbarMessage('Password changed successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Reset form and close dialog
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setChangingPassword(false);
      setChangePasswordDialogOpen(false);
      handleUserMenuClose();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to change password';
      setPasswordError(errorMessage);
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.phoneNumber) {
      return;
    }
    
    try {
      setDeletingAccount(true);
      
      if (user.userType === 'VENDOR') {
        await VendorService.deleteVendor(user.phoneNumber, user.phoneNumber);
      } else {
        await UserService.deleteUser(user.phoneNumber, user.phoneNumber);
      }
      
      // Show success message
      setSnackbarMessage('Your account and all related data have been deleted successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Close dialogs
      setDeletingAccount(false);
      setDeleteAccountDialogOpen(false);
      handleUserMenuClose();
      
      // Wait a moment for user to see the message, then logout and redirect
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 2000);
    } catch (err: any) {
      console.error('Error deleting account:', err);
      // Extract error message from backend response
      let errorMessage = 'Failed to delete account.';
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDeletingAccount(false);
      // Don't close the dialog if there's an error - let user see the message
      // setDeleteAccountDialogOpen(false);
      // handleUserMenuClose();
    }
  };

  // VendorNotificationContext handles all vendor notification fetching and syncing
  // No need for separate useEffect hooks here

  // NotificationContext handles all notification fetching and syncing
  // No need for separate useEffect hooks here

  // Fetch unread chat messages for both clients and vendors
  React.useEffect(() => {
    if (user) {
      const fetchUnreadChatCount = async () => {
        try {
          const count = await chatService.getTotalUnreadCount(user.phoneNumber);
          setUnreadChatCount(count);
        } catch (error) {
          console.error('Error fetching unread chat count:', error);
        }
      };
      
      fetchUnreadChatCount();
      // Refresh every 15 seconds for chat messages
      const interval = setInterval(fetchUnreadChatCount, 15000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Fetch vendor's business to determine if it's a catering or tent business
  React.useEffect(() => {
    if (user && user.userType === 'VENDOR' && user.phoneNumber) {
      const fetchVendorBusiness = async () => {
        try {
          const businesses = await BusinessService.getBusinessesByVendorPhoneNumber(user.phoneNumber);
          // Check if any business is catering type
          const hasCateringBusiness = businesses.some(b => b.businessCategory === 'caters');
          setIsCateringBusiness(hasCateringBusiness);
          
          // Store all business categories for vendor profile
          const categories = businesses.map(b => b.businessCategory).filter(Boolean);
          setVendorCategories(categories);
        } catch (error) {
          console.error('Error fetching vendor business:', error);
          setIsCateringBusiness(false);
          setVendorCategories([]);
        }
      };
      
      fetchVendorBusiness();
    } else {
      setIsCateringBusiness(false);
      setVendorCategories([]);
    }
  }, [user]);

  const baseItems = [
    { text: 'Explore', icon: <PaletteIcon />, path: '/explore' },
  ];

  // Client items removed from sidebar - now in profile dropdown
  const clientItems: any[] = [];

  // Vendor menu items - dynamically change Theme to Plate for catering businesses
  // Add Inventory for tent businesses
  const getVendorItems = () => {
    const baseVendorItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/vendor-dashboard' },
      { text: 'Business', icon: <BusinessIcon />, path: '/vendor-dashboard' },
      // Theme and Inventory options disabled - removed from navigation
      // { text: isCateringBusiness ? 'Plate' : 'Theme', icon: <PaletteIcon />, path: isCateringBusiness ? '/plates' : '/themes' },
      // Dish tab removed for catering businesses
      // { text: 'Inventory', icon: <InventoryIcon />, path: '/vendor-dashboard', activeTab: 2 } - disabled
      { text: 'Availability', icon: <CalendarIcon />, path: '/availability' },
    ];
    return baseVendorItems;
  };

  const vendorItems = getVendorItems();

  const adminItems = [
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Vendors', icon: <BusinessIcon />, path: '/vendors' },
    { text: 'Businesses', icon: <StoreIcon />, path: '/businesses' },
    { text: 'Themes', icon: <PaletteIcon />, path: '/themes' },
    { text: 'Images', icon: <ImageIcon />, path: '/images' },
  ];

  // Determine menu items based on user role and type
  // Priority: SUPER_ADMIN > VENDOR (by userType) > CLIENT > ADMIN/VENDOR_ADMIN > default
  // IMPORTANT: Check userType BEFORE role to ensure vendors always get vendor menu
  let menuItems = baseItems;
  
  if (user) {
    if (user.role === 'SUPER_ADMIN') {
      // Super admin sees everything
      menuItems = [
        { text: 'Super Admin Dashboard', icon: <DashboardIcon />, path: '/super-admin-dashboard' },
        ...baseItems,
        ...adminItems
      ];
    } else if (user.userType === 'VENDOR') {
      // Vendors ONLY see vendor-specific items (check userType FIRST before role)
      menuItems = vendorItems;
    } else if (user.userType === 'CLIENT') {
      // Clients see client-specific items
      menuItems = clientItems;
    } else if (user.role === 'ADMIN' || user.role === 'VENDOR_ADMIN') {
      // Admin sees admin dashboard and admin items (only if not VENDOR userType)
      menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        ...baseItems,
        ...adminItems
      ];
    }
  }

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          {APP_DISPLAY_NAME}
        </Typography>
      </Toolbar>
      <List>
        {menuItems.map((item) => {
          const hasActiveTab = (item as any).activeTab !== undefined;
          const isSelected = hasActiveTab 
            ? location.pathname === item.path && (location.state as any)?.activeTab === (item as any).activeTab
            : location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={hasActiveTab ? 'div' : Link}
                to={hasActiveTab ? undefined : item.path}
                onClick={hasActiveTab ? () => {
                  setMobileOpen(false); // Close mobile drawer
                  const targetTab = (item as any).activeTab;
                  // Force navigation with state - add timestamp to ensure state change is detected
                  navigate(item.path, { 
                    state: { 
                      activeTab: targetTab,
                      _timestamp: Date.now() // Force state update
                    },
                    replace: false // Always push to ensure state is updated
                  });
                } : undefined}
                selected={isSelected}
              >
                <ListItemIcon>
                  {item.path.includes('chat') && unreadChatCount > 0 ? (
                    <Badge badgeContent={unreadChatCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  const isClientExplore = Boolean(
    user?.userType === 'CLIENT' && location.pathname === '/explore'
  );
  const isClientChat = Boolean(
    user?.userType === 'CLIENT' && location.pathname === '/client-chat'
  );
  const isClientDashboard = Boolean(
    user?.userType === 'CLIENT' && location.pathname === '/client-dashboard'
  );
  const isClientRatings = Boolean(
    user?.userType === 'CLIENT' && location.pathname === '/client-ratings'
  );
  const isChaaviClientShell =
    isClientExplore || isClientChat || isClientDashboard || isClientRatings;
  /** Vendors on Explore use the same warm Chaavi top bar + search as clients (peek the marketplace). */
  const isVendorExplore = Boolean(
    user?.userType === 'VENDOR' && location.pathname === '/explore'
  );
  const isChaaviTopbar = isChaaviClientShell || isVendorExplore;
  /** Vendor dashboard & availability embed Chaavi sidebar + topbar — hide global chrome to avoid duplicate nav. */
  const isChaaviVendorShell = Boolean(
    user?.userType === 'VENDOR' &&
      (location.pathname === '/vendor-dashboard' ||
        location.pathname === '/availability' ||
        location.pathname === '/vendor-chat')
  );

  return (
    <>
      {!isChaaviVendorShell && (
      <AppBar
        position="fixed"
        sx={{
          width: { sm: user?.userType === 'CLIENT' ? '100%' : `calc(100% - clamp(240px, 30vw, 280px))` },
          ml: { sm: user?.userType === 'CLIENT' ? 0 : 'clamp(240px, 30vw, 280px)' },
          ...(isChaaviTopbar
            ? {
                background: 'rgba(253, 249, 243, 0.96)',
                backdropFilter: 'blur(14px)',
                borderBottom: '1px solid rgba(139, 105, 20, 0.2)',
                color: '#2e261c',
                boxShadow: '0 1px 0 rgba(46, 38, 28, 0.06)',
              }
            : {}),
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            gap: isChaaviTopbar ? 2 : undefined,
          }}
        >
          {user?.userType !== 'CLIENT' && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {isChaaviTopbar ? (
            <MuiTypography
              component={Link}
              to="/explore"
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 22,
                fontWeight: 400,
                color: '#7a5c1a',
                letterSpacing: '0.06em',
                textDecoration: 'none',
                '&:hover': { color: '#5c4512' },
              }}
            >
              <span aria-hidden>{isClientChat ? '🪔' : '🎊'}</span>
              {APP_DISPLAY_NAME.toLowerCase()}
            </MuiTypography>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}
          {user && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                justifyContent: 'center',
                maxWidth: isChaaviTopbar
                  ? { xs: '100%', sm: 520 }
                  : { xs: '100%', sm: '400px', md: '500px' },
                mx: 'auto',
                position: 'relative',
              }}
            >
              {/* Search on Explore (all) and Client Chat */}
              {((user.userType === 'CLIENT' &&
                (location.pathname === '/explore' || location.pathname === '/client-chat')) ||
                (user.userType === 'VENDOR' && location.pathname === '/explore')) && (
                <>
                  <Paper
                    component="form"
                    sx={{
                      p: '2px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      maxWidth: 520,
                      borderRadius: '20px',
                      ...(isChaaviTopbar
                        ? {
                            bgcolor: '#fffef9',
                            border: '1px solid rgba(139, 105, 20, 0.22)',
                            '&:hover': { bgcolor: '#faf6f0' },
                          }
                        : {
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                            },
                          }),
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <InputBase
                      sx={{
                        ml: 1,
                        flex: 1,
                        fontSize: '0.95rem',
                        color: isChaaviTopbar ? '#2e261c' : undefined,
                        '& .MuiInputBase-input::placeholder': {
                          color: isChaaviTopbar ? 'rgba(46, 38, 28, 0.4)' : undefined,
                          opacity: 1,
                        },
                      }}
                      placeholder="Search themes, food, vendors…"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSuggestions(e.target.value.length >= 2);
                      }}
                      onFocus={(e) => {
                        setSearchAnchorEl(e.currentTarget);
                        if (searchQuery.length >= 2) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setShowSuggestions(false);
                          setSearchAnchorEl(null);
                        }, 200);
                      }}
                      inputProps={{ 'aria-label': 'search' }}
                    />
                    <IconButton
                      type="button"
                      sx={{
                        p: '8px',
                        color: isChaaviTopbar ? '#b8860b' : 'primary.main',
                      }}
                      aria-label="search"
                    >
                      <SearchIcon />
                    </IconButton>
                  </Paper>
                  
                  {/* Auto-suggestions */}
                  <Popper
                    open={showSuggestions && searchSuggestions.length > 0}
                    anchorEl={searchAnchorEl}
                    placement="bottom-start"
                    style={{ width: searchAnchorEl?.clientWidth, zIndex: 1300 }}
                  >
                    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
                      <Paper
                        sx={{
                          mt: 1,
                          width: '100%',
                          maxHeight: 300,
                          overflow: 'auto',
                          boxShadow: 4,
                          borderRadius: 2,
                          ...(isChaaviTopbar
                            ? {
                                bgcolor: '#fffef9',
                                border: '1px solid rgba(139, 105, 20, 0.22)',
                                color: '#2e261c',
                              }
                            : {}),
                        }}
                      >
                        <List dense>
                          {searchSuggestions.map((suggestion, index) => (
                            <ListItem
                              key={`${suggestion.type}-${suggestion.id}-${index}`}
                              button
                              onClick={() => {
                                setSearchQuery(suggestion.name);
                                setShowSuggestions(false);
                                setSearchAnchorEl(null);
                                // Dispatch event to scroll to item
                                window.dispatchEvent(new CustomEvent('scrollToSearchItem', { 
                                  detail: { type: suggestion.type, id: suggestion.id } 
                                }));
                              }}
                              sx={{
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                              }}
                            >
                              <ListItemIcon>
                                {suggestion.type === 'theme' && <PaletteIcon color="primary" />}
                                {suggestion.type === 'inventory' && <InventoryIcon color="primary" />}
                                {suggestion.type === 'plate' && <RestaurantIcon color="primary" />}
                                {suggestion.type === 'dish' && <LocalDiningIcon color="primary" />}
                              </ListItemIcon>
                              <ListItemText
                                primary={suggestion.name}
                                secondary={
                                  suggestion.type === 'theme' ? 'Theme' :
                                  suggestion.type === 'inventory' ? 'Inventory Item' :
                                  suggestion.type === 'plate' ? 'Plate' : 'Dish'
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                </>
              )}
            </Box>
          )}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Filter Icon for Clients on Explore Page - positioned just right of search */}
              {location.pathname === '/explore' &&
                (user.userType === 'CLIENT' || user.userType === 'VENDOR') && (
                <IconButton
                  color="inherit"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('openFilterDialog'));
                  }}
                  sx={{ mr: 1 }}
                >
                  <FilterIcon />
                </IconButton>
              )}
              {/* Notification Icon for Clients */}
              {user.userType === 'CLIENT' && (
                <>
                  <IconButton
                    color="inherit"
                    onClick={handleNotificationMenuOpen}
                    sx={{ mr: 1 }}
                  >
                    <Badge
                      badgeContent={clientNotificationCount > 0 ? clientNotificationCount : undefined}
                      invisible={clientNotificationCount === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          minWidth: 16,
                          height: 16,
                          bgcolor: '#c0873a',
                          color: '#fff',
                        },
                      }}
                    >
                      <NotificationIcon />
                    </Badge>
                  </IconButton>
                  <ChaaviNotificationPanel
                    anchorEl={notificationMenuAnchor}
                    open={Boolean(notificationMenuAnchor)}
                    onClose={handleNotificationMenuClose}
                    notifications={clientNotifications}
                    loading={loadingNotifications}
                    onItemClick={notification => {
                      void handleNotificationClick(notification);
                    }}
                    onMarkAllRead={() => {
                      void markAllClientNotificationsRead();
                    }}
                    onDismissItem={notification => {
                      void handleClientNotificationDismiss(notification);
                    }}
                    onViewAll={() => {
                      handleNotificationMenuClose();
                      navigate('/client-dashboard', { state: { activeTab: 2 } });
                    }}
                    viewAllLabel="View all activity"
                  />
                </>
              )}
              
              {/* Cart Icon for Clients */}
              {user.userType === 'CLIENT' && (
                <IconButton
                  color="inherit"
                  onClick={openCart}
                  sx={{ mr: 1 }}
                >
                  <Badge badgeContent={getCartItemCount() > 0 ? getCartItemCount() : 0} color="error">
                    <CartIcon />
                  </Badge>
                </IconButton>
              )}
              
              {/* Notification Icon for Vendors */}
              {user.userType === 'VENDOR' && (
                <>
                  <IconButton
                    color="inherit"
                    onClick={handleVendorNotificationMenuOpen}
                    sx={{ mr: 1 }}
                  >
                    <Badge
                      badgeContent={vendorNotificationCount > 0 ? vendorNotificationCount : undefined}
                      invisible={vendorNotificationCount === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          minWidth: 16,
                          height: 16,
                          bgcolor: '#c0873a',
                          color: '#fff',
                        },
                      }}
                    >
                      <NotificationIcon />
                    </Badge>
                  </IconButton>
                  <ChaaviNotificationPanel
                    anchorEl={vendorNotificationMenuAnchor}
                    open={Boolean(vendorNotificationMenuAnchor)}
                    onClose={handleVendorNotificationMenuClose}
                    notifications={vendorNotifications}
                    loading={loadingVendorNotifications}
                    onItemClick={notification => {
                      void handleVendorNotificationClick(notification);
                    }}
                    onMarkAllRead={() => {
                      void markAllVendorNotificationsRead();
                    }}
                    onDismissItem={notification => {
                      void handleVendorNotificationDismiss(notification);
                    }}
                    onViewAll={() => {
                      handleVendorNotificationMenuClose();
                      if (isCateringBusiness) {
                        navigate('/vendor-dashboard', { state: { activeTab: 4 } });
                      } else {
                        navigate('/vendor-orders-notifications');
                      }
                    }}
                    viewAllLabel="View all activity"
                  />
                </>
              )}
              
              {/* Chat Icon for Both Clients and Vendors */}
              <IconButton
                color="inherit"
                onClick={() => navigate(user.userType === 'CLIENT' ? '/client-chat' : '/vendor-chat')}
                sx={{ mr: 1 }}
              >
                <Badge badgeContent={unreadChatCount > 0 ? unreadChatCount : 0} color="error">
                  <ChatIcon />
                </Badge>
              </IconButton>
              <Typography 
                variant="body2" 
                sx={{ 
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: 'clamp(0.75rem, 2vw, 0.875rem)', sm: 'clamp(0.875rem, 1.5vw, 1rem)' },
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                Welcome, {user.firstName}
              </Typography>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
              >
              <Avatar sx={{ 
                width: { xs: 'clamp(28px, 4vw, 32px)', sm: 'clamp(32px, 4vw, 40px)' },
                height: { xs: 'clamp(28px, 4vw, 32px)', sm: 'clamp(32px, 4vw, 40px)' },
                fontSize: { xs: 'clamp(0.875rem, 2vw, 1rem)', sm: 'clamp(1rem, 2vw, 1.25rem)' }
              }}>
                {user.firstName.charAt(0).toUpperCase()}
              </Avatar>
              </IconButton>
              {user.userType === 'CLIENT' ? (
                <ChaaviClientProfileMenu
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleUserMenuClose}
                  user={user}
                  ordersNewCount={clientNotificationCount}
                  onMyOrders={() => navigate('/client-dashboard')}
                  onChat={() => navigate('/client-chat')}
                  onMyRatings={() => navigate('/client-ratings')}
                  onEditProfile={() => setClientEditProfileOpen(true)}
                  onChangePassword={() => setChangePasswordDialogOpen(true)}
                  onDeleteAccount={() => setDeleteAccountDialogOpen(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Menu
                  id="user-menu"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                >
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </MenuItem>
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      Role: {user.userType}
                    </Typography>
                  </MenuItem>
                  {user.userType === 'VENDOR' && vendorCategories.length > 0 && (
                    <MenuItem disabled>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Category:
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                          {vendorCategories.map((category, index) => (
                            <Chip
                              key={index}
                              label={category}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      setChangePasswordDialogOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <LockIcon fontSize="small" />
                    </ListItemIcon>
                    Change Password
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      setDeleteAccountDialogOpen(true);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    Delete Account
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      )}
      {!isChaaviVendorShell && user?.userType !== 'CLIENT' && (
        <Box
          component="nav"
          sx={{ 
            width: { sm: 'clamp(240px, 30vw, 280px)' }, 
            flexShrink: { sm: 0 } 
          }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 'clamp(240px, 70vw, 280px)',
                padding: 'clamp(0.5rem, 1vw, 1rem)'
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 'clamp(240px, 30vw, 280px)',
                padding: 'clamp(0.5rem, 1vw, 1rem)'
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}
      <Cart open={isCartOpen} onClose={closeCart} />

      {user?.userType === 'CLIENT' && (
        <ChaaviEditProfileModal
          open={clientEditProfileOpen}
          onClose={() => {
            if (editProfileSaving) return;
            setClientEditProfileOpen(false);
            setEditProfileError('');
          }}
          firstName={editFirstName}
          lastName={editLastName}
          email={editEmail}
          phoneNumber={user.phoneNumber}
          onFirstNameChange={v => {
            setEditFirstName(v);
            setEditProfileError('');
          }}
          onLastNameChange={v => {
            setEditLastName(v);
            setEditProfileError('');
          }}
          onEmailChange={v => {
            setEditEmail(v);
            setEditProfileError('');
          }}
          saving={editProfileSaving}
          error={editProfileError}
          onSave={() => void handleSaveClientProfile()}
        />
      )}

      <ChaaviChangePasswordModal
        open={changePasswordDialogOpen}
        onClose={() => {
          if (changingPassword) return;
          setChangePasswordDialogOpen(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordError('');
        }}
        currentPassword={currentPassword}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        onCurrentPasswordChange={v => {
          setCurrentPassword(v);
          setPasswordError('');
        }}
        onNewPasswordChange={v => {
          setNewPassword(v);
          setPasswordError('');
        }}
        onConfirmPasswordChange={v => {
          setConfirmPassword(v);
          setPasswordError('');
        }}
        submitting={changingPassword}
        serverError={passwordError}
        onSubmit={handleChangePassword}
      />

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteAccountDialogOpen}
        onClose={() => !deletingAccount && setDeleteAccountDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">
          Delete {user?.userType === 'VENDOR' ? 'Vendor' : 'Client'} Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account?
            <br /><br />
            <strong>Note:</strong> You cannot delete your account if you have:
            <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
              <li><strong>Request orders</strong> (pending orders that are waiting for confirmation)</li>
              <li><strong>Incomplete orders</strong> (orders that are confirmed, preparing, or ready but not yet delivered or cancelled)</li>
            </ul>
            Please complete or cancel all orders before deleting your account.
            <br /><br />
            This action will permanently delete:
            <ul>
              {user?.userType === 'VENDOR' ? (
                <>
                  <li>Your vendor profile</li>
                  <li>All your businesses and their data</li>
                  <li>All themes, inventory, plates, and dishes</li>
                  <li>All images associated with your businesses</li>
                  <li>All orders and order history</li>
                  <li>All notifications and chat messages</li>
                </>
              ) : (
                <>
                  <li>Your account profile</li>
                  <li>All your orders and order history</li>
                  <li>All notifications</li>
                  <li>All ratings you've given</li>
                  <li>All chat messages and conversations</li>
                </>
              )}
            </ul>
            <strong>This action cannot be undone. You will be logged out immediately.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteAccountDialogOpen(false)} 
            disabled={deletingAccount}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={deletingAccount}
            startIcon={deletingAccount ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingAccount ? 'Deleting Account...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Navigation;
